import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import api from "../../../utils/api";
import dayjs from "dayjs";

const EmailCostByDomain = ({
  startDate,
  endDate,
  selectedRange,
  format = "day",
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, selectedRange, format]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      let from, to;

      if (selectedRange === "custom" && startDate && endDate) {
        from = startDate;
        to = endDate;
      } else if (selectedRange === "7_days") {
        from = dayjs().subtract(7, "day");
        to = dayjs();
      } else if (selectedRange === "30_days") {
        from = dayjs().subtract(30, "day");
        to = dayjs();
      } else if (selectedRange === "year") {
        from = dayjs().subtract(1, "year");
        to = dayjs();
      } else {
        from = dayjs().subtract(7, "day");
        to = dayjs();
      }

      const response = await api.get("/admin/statistics/email/cost-by-domain", {
        params: {
          from: from.format("YYYY-MM-DD"),
          to: to.format("YYYY-MM-DD"),
          format: format,
        },
      });

      if (response.data.success) {
        setData(response.data.data.statistics);
      } else {
        setError("Failed to load data");
      }
    } catch (err) {
      console.error("Error fetching email cost by domain:", err);
      setError(err.response?.data?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card sx={{ height: 500 }}>
        <CardContent
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <Card sx={{ height: 500 }}>
        <CardContent sx={{ height: "100%" }}>
          <Typography variant="h6" gutterBottom>
            Cost by Domain
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {error || "No data available"}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data - show all domains (or limit to top 50 for performance)
  const topDomains = data.slice(0, 50);
  const domains = topDomains.map((item) => item.domain);
  const costs = topDomains.map((item) => item.total_cost);

  return (
    <Card sx={{ height: 800 }}>
      <CardContent
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Typography variant="h6" gutterBottom>
          Cost by Domain
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Total email costs per domain{" "}
          {data.length > 50
            ? `(Top 50 of ${data.length})`
            : `(${data.length} domains)`}
        </Typography>
        <Box sx={{ width: "100%", flex: 1, minHeight: 0 }}>
          <BarChart
            layout="horizontal"
            xAxis={[
              {
                valueFormatter: (value) =>
                  `$${value?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`,
              },
            ]}
            yAxis={[
              {
                scaleType: "band",
                data: domains,
                width: 130,
              },
            ]}
            series={[
              {
                data: costs,
                label: "Total Cost",
                color: "#1976d2",
                valueFormatter: (value) => `$${value}`,
              },
            ]}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default EmailCostByDomain;
